/**
 * The slice order a saved stone's voxels are in (ENG-258).
 * - `list`: saved from a stack before the fix; stored slice s is the series' image s.
 * - `volume`: the source volume's slice order (volume saves, and every save since the fix).
 * - `unknown`: the record can't tell; the loader uses the file header's default.
 */
export type SavedStoneOrder = "list" | "volume" | "unknown";

export interface SavedStoneRecord {
  segment_index: number;
  bounding_box?: Record<string, number> | null;
  /** Set by the backend record rewrite; not returned by the API yet. */
  corrected_by?: unknown;
  companion_annotations?: Array<{ companion_id: string }>;
}

/**
 * Before the fix, the backend computed `abs_z` with the save's own slice
 * spacing, so `abs_z2 / point2_k` is about 0.001 for a stack save and the real
 * spacing for a volume save. A record the backend rewrote carries `corrected_by`.
 */
export function savedStoneOrder(record: SavedStoneRecord): SavedStoneOrder {
  if (record.corrected_by === "ENG-258") return "list";
  const k = record.bounding_box?.point2_k;
  const z = record.bounding_box?.abs_z2;
  if (typeof k !== "number" || !(k > 0) || typeof z !== "number" || !Number.isFinite(z)) {
    return "unknown";
  }
  return Math.abs(z / k) < 0.01 ? "list" : "volume";
}

/** Stored label → order, for a primary mask. */
export function labelOrdersOf(records: readonly SavedStoneRecord[]): Map<number, SavedStoneOrder> {
  return new Map(records.map((record) => [record.segment_index, savedStoneOrder(record)]));
}

/** Stored label → order, for a companion mask: a companion record has no
 *  `bounding_box`, so its stone takes its owning primary record's order. */
export function companionLabelOrders(
  records: readonly SavedStoneRecord[],
  companionId: string,
): Map<number, SavedStoneOrder> {
  return labelOrdersOf(
    records.filter((record) =>
      record.companion_annotations?.some((comp) => comp.companion_id === companionId),
    ),
  );
}
